import {BASE_URL, HandleErrorResponse} from "../common";
import {
    AccountStackSetDriftInfo,
    DriftDetails,
    DriftedAccountRequest
} from "../../utils/types/stackSetDrift";
import {DriftDetectionApiService, ScpDriftApiService} from "./service"
import {GenericServiceSummary} from "../../utils/types/genericServiceSummaryItem";
import { ScpDriftInfo, ScpDriftRequest } from "../../utils/types/scpDrift";

export class MainDriftDetectionApiService implements DriftDetectionApiService {
    readonly basePath = `${BASE_URL}/stackset-drift`;

    async getStackSetDrift(filter: DriftedAccountRequest): Promise<AccountStackSetDriftInfo[]> {
        try {
            const finalURI = `${this.basePath}?account_id=${filter.account_id}&stackset_name=${filter.stackset_name}&from_time=${Math.round(filter.from_time.getTime() / 1000)}&to_time=${Math.round(filter.to_time.getTime() / 1000)}`;
            const response = await fetch(finalURI, {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('access_token')
                },
            });

            if (!response.ok) {
                await HandleErrorResponse(response);
            }

            const jsonArray: any[] = await response.json();
            return jsonArray.map((item, index) => {
                return asPCSKDriftDetectionItem(item, index + 1);
            });

        } catch (error) {
            console.error("Error fetching stack set drift:", error);
            throw error;
        }
    }

    async getStackSetDriftSummary(): Promise<GenericServiceSummary> {
        try {
            const finalURI = `${BASE_URL}/pcsk-admin/summary/drift/stacksets`;
            const response = await fetch(finalURI, {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('access_token')
                },
            });

            if (!response.ok) {
                await HandleErrorResponse(response);
            }

            const respJson: any[] = await response.json();
            return GenericServiceSummary.fromJSON(respJson);

        } catch (error) {
            console.error("Error fetching stack set drift summary :", error);
            throw error;
        }
    }
}
const asPCSKDriftDetectionItem = (response: any, index = 1): AccountStackSetDriftInfo => {
    let drift_details: DriftDetails[] = [];
    if (response.drift_details) {
        drift_details = JSON.parse(response.drift_details).map((detail: any) => {
            return {
                LogicalResourceId: detail.LogicalResourceId,
                ResourceType: detail.ResourceType,
                StackId: detail.StackId,
                PhysicalResourceId: detail.PhysicalResourceId,
                StackResourceDriftStatus: detail.StackResourceDriftStatus,
                PropertyDifferences: detail.PropertyDifferences.map((diff: any) => {
                    return {
                        PropertyPath: diff.PropertyPath,
                        ExpectedValue: diff.ExpectedValue,
                        ActualValue: diff.ActualValue,
                        DifferenceType: diff.DifferenceType
                    };
                })
            };
        });
    }

    return {
        index: index,
        account_id: response.account_id,
        stackset_name: response.stackset_name,
        drift_status: response.drift_status,
        false_positive: response.false_positive,
        status_reason: response.status_reason,
        instance_status: response.instance_status,
        drift_check_timestamp: response.drift_check_time,
        drift_details: drift_details
    }
};

export class MainScpDriftApiService implements ScpDriftApiService {
    readonly basePath = `${BASE_URL}/scp-drift`;

    async getScpDrift(filter: ScpDriftRequest): Promise<ScpDriftInfo[]> {
        try {
            // Build query parameters
            const params = new URLSearchParams();
            params.append('from_timestamp', filter.from_timestamp.toString());
            params.append('to_timestamp', filter.to_timestamp.toString());
            if (filter.scp_name) {
                params.append('scp_name', filter.scp_name);
            }
            if (filter.mpa_id) {
                params.append('mpa_id', filter.mpa_id);
            }
            
            const finalURI = `${this.basePath}?${params.toString()}`;
            const response = await fetch(finalURI, {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('access_token')
                },
            });

            if (!response.ok) {
                await HandleErrorResponse(response);
            }

            const jsonArray: any[] = await response.json();
            // Debug: Log to verify code is running
            console.log("✅ SCP Drift API Response received:", jsonArray.length, "items");
            if (jsonArray.length > 0) {
                console.log("✅ First item timestamp:", jsonArray[0].drift_check_timestamp);
            }
            return jsonArray.map((item, index) => {
                return asScpDriftItem(item, index + 1);
            });

        } catch (error) {
            console.error("Error fetching SCP drift:", error);
            throw error;
        }
    }
}

// Transform raw API response to typed ScpDriftInfo object
const asScpDriftItem = (response: any, index = 1): ScpDriftInfo => {
    // Handle drift_check_timestamp - check for null/undefined/0 explicitly
    let driftCheckTimestamp: number;
    if (response.drift_check_timestamp !== undefined && response.drift_check_timestamp !== null && response.drift_check_timestamp !== 0) {
        driftCheckTimestamp = response.drift_check_timestamp;
    } else if (response.driftCheckTimestamp !== undefined && response.driftCheckTimestamp !== null && response.driftCheckTimestamp !== 0) {
        driftCheckTimestamp = response.driftCheckTimestamp;
    } else {
        // Only use fallback if truly missing
        console.warn(`Missing drift_check_timestamp for item ${index}, using current time as fallback`);
        driftCheckTimestamp = Math.floor(Date.now() / 1000);
    }
    
    return {
        index: index,
        scp_name: response.scp_name || response.scpName || "",
        mpa_id: response.mpa_id || response.mpaId || "",
        drift_check_timestamp: driftCheckTimestamp,
        is_falcon: response.is_falcon !== undefined ? response.is_falcon : (response.isFalcon !== undefined ? response.isFalcon : false),
        target_id: response.target_id || response.targetId || "",
        target_path: response.target_path || response.targetPath || "",
        target_type: response.target_type || response.targetType || "",
    };
};
